/**
 * Photo Gallery JavaScript
 * Handles layout switching, pagination, infinite scroll, Lightbox integration, downloads, and Cloudinary folder loading
 */

console.log('[PhotoGallery] Script loaded');

// Prevent redeclaration if script loads multiple times
if (typeof PhotoGallery === 'undefined') {
  
class PhotoGallery {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.layout = container.dataset.layout || 'simple_grid';
    this.structure = container.dataset.structure || 'all';
    this.loadMethod = container.dataset.loadMethod || 'all';
    this.itemsPerPage = parseInt(container.dataset.itemsPerPage) || 12;
    this.columnsMobile = container.dataset.columnsMobile || '2';
    this.columnsDesktop = container.dataset.columnsDesktop || '4';
    
    this.currentPage = 1;
    this.currentPageByTab = {}; // Track current page per tab/folder
    this.activeFolderPath = 'all'; // Track the currently active folder path
    this.allItems = [];
    this.visibleItems = [];
    this.isLoading = false;
    
    // Get Cloudinary settings from section
    // Try to find the container element first (it has the data attributes)
    let sectionElement = this.container;
    
    // If container doesn't have the attributes, try to find the section wrapper
    if (!sectionElement.dataset.imageSource) {
      sectionElement = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    }
    
    // Also try the parent section wrapper
    if (!sectionElement || !sectionElement.dataset.imageSource) {
      const sectionWrapper = this.container.closest('.photo-gallery-section');
      if (sectionWrapper) {
        const sectionIdAttr = sectionWrapper.querySelector(`[data-section-id="${this.sectionId}"]`);
        if (sectionIdAttr) {
          sectionElement = sectionIdAttr;
        }
      }
    }
    
    console.log('[PhotoGallery] Section element found:', sectionElement);
    console.log('[PhotoGallery] Container element:', this.container);
    console.log('[PhotoGallery] All data attributes on container:', {
      sectionId: this.container.dataset.sectionId,
      imageSource: this.container.dataset.imageSource,
      cloudinaryCloudName: this.container.dataset.cloudinaryCloudName,
      cloudinaryApiKey: this.container.dataset.cloudinaryApiKey ? 'set' : 'not set',
      cloudinaryApiSecret: this.container.dataset.cloudinaryApiSecret ? 'set' : 'not set'
    });
    
    // Read from container first (where we set the attributes)
    this.cloudinaryCloudName = this.container.dataset.cloudinaryCloudName || sectionElement?.dataset.cloudinaryCloudName || '';
    this.imageSource = this.container.dataset.imageSource || sectionElement?.dataset.imageSource || 'metaobject';
    this.cloudinaryApiKey = this.container.dataset.cloudinaryApiKey || sectionElement?.dataset.cloudinaryApiKey || '';
    this.cloudinaryApiSecret = this.container.dataset.cloudinaryApiSecret || sectionElement?.dataset.cloudinaryApiSecret || '';
    this.cloudinaryProxyUrl = this.container.dataset.cloudinaryProxyUrl || sectionElement?.dataset.cloudinaryProxyUrl || '';
    
    console.log('[PhotoGallery] Final settings:', {
      imageSource: this.imageSource,
      cloudinaryCloudName: this.cloudinaryCloudName,
      cloudinaryApiKey: this.cloudinaryApiKey ? 'set' : 'not set',
      cloudinaryApiSecret: this.cloudinaryApiSecret ? 'set' : 'not set'
    });
    
    this.init();
  }

  init() {
    console.log('[PhotoGallery] Initializing gallery...', {
      sectionId: this.sectionId,
      imageSource: this.imageSource,
      cloudinaryCloudName: this.cloudinaryCloudName,
      cloudinaryApiKey: this.cloudinaryApiKey ? 'set' : 'not set',
      cloudinaryApiSecret: this.cloudinaryApiSecret ? 'set' : 'not set'
    });
    
    this.setupCSSVariables();
    
    // If using Cloudinary, load images from folders first
    if (this.imageSource === 'cloudinary' && this.cloudinaryCloudName) {
      console.log('[PhotoGallery] Cloudinary mode detected, loading folders...');
      this.loadCloudinaryFolders().then(() => {
        console.log('[PhotoGallery] Cloudinary folders loaded, setting up gallery...');
        this.collectItems();
        this.setupTabs();
        this.setupPagination();
        this.setupInfiniteScroll();
        this.setupDownloads();
        this.setupLightbox();
        this.applyLayout();
        
        // Update infinite scroll sentinel after initial setup
        if (this.loadMethod === 'infinite_scroll') {
          setTimeout(() => {
            this.updateInfiniteScrollSentinel();
          }, 300);
        }
      }).catch((error) => {
        console.error('[PhotoGallery] Error loading Cloudinary folders:', error);
      });
    } else {
      console.log('[PhotoGallery] Non-Cloudinary mode, setting up gallery...');
      this.collectItems();
      this.setupTabs();
      this.setupPagination();
      this.setupInfiniteScroll();
      this.setupDownloads();
      this.setupLightbox();
      this.applyLayout();
      
      // Update infinite scroll sentinel after initial setup
      if (this.loadMethod === 'infinite_scroll') {
        setTimeout(() => {
          this.updateInfiniteScrollSentinel();
        }, 300);
      }
    }
  }
  
  async loadCloudinaryFolders() {
    const loadingGrids = this.container.querySelectorAll('[data-cloudinary-loading="true"]');
    if (loadingGrids.length === 0) {
      console.log('[PhotoGallery] No loading grids found');
      return;
    }
    
    console.log(`[PhotoGallery] Found ${loadingGrids.length} loading grid(s)`);
    
    // Get folder paths from container data attribute (where we set it)
    // HTML data attributes with dashes become camelCase: data-cloudinary-folders -> cloudinaryFolders
    let folderData = this.container.dataset.cloudinaryFolders;
    
    console.log('[PhotoGallery] Folder data from container.dataset.cloudinaryFolders:', folderData);
    console.log('[PhotoGallery] Container dataset keys:', Object.keys(this.container.dataset));
    
    // Also try reading the attribute directly
    if (!folderData) {
      folderData = this.container.getAttribute('data-cloudinary-folders');
      console.log('[PhotoGallery] Folder data from getAttribute:', folderData);
    }
    
    if (!folderData) {
      // Try to get from section element
      const sectionElement = document.querySelector(`[data-section-id="${this.sectionId}"]`);
      folderData = sectionElement?.dataset.cloudinaryFolders || sectionElement?.getAttribute('data-cloudinary-folders');
      console.log('[PhotoGallery] Folder data from section element:', folderData);
    }
    
    if (!folderData) {
      console.warn('[PhotoGallery] No Cloudinary folder data found');
      console.warn('[PhotoGallery] Container HTML:', this.container.outerHTML.substring(0, 500));
      loadingGrids.forEach(grid => {
        const loadingEl = grid.querySelector('.photo-gallery-loading');
        if (loadingEl) loadingEl.innerHTML = '<p>No folders configured</p>';
      });
      return;
    }
    
    // Clean up the JSON string (remove newlines and extra whitespace)
    folderData = folderData.trim().replace(/\s+/g, ' ');
    
    let folders = [];
    try {
      folders = JSON.parse(folderData);
      console.log('[PhotoGallery] Parsed folders:', folders);
    } catch (e) {
      console.error('[PhotoGallery] Error parsing folder data:', e);
      console.error('[PhotoGallery] Raw data:', folderData);
      console.error('[PhotoGallery] Data type:', typeof folderData);
      loadingGrids.forEach(grid => {
        const loadingEl = grid.querySelector('.photo-gallery-loading');
        if (loadingEl) loadingEl.innerHTML = '<p>Error parsing folder data</p>';
      });
      return;
    }
    
    if (!Array.isArray(folders) || folders.length === 0) {
      console.warn('[PhotoGallery] No folders in array:', folders);
      loadingGrids.forEach(grid => {
        const loadingEl = grid.querySelector('.photo-gallery-loading');
        if (loadingEl) loadingEl.innerHTML = '<p>No folders configured</p>';
      });
      return;
    }
    
    // Normalize all folder paths to ensure consistency
    folders = folders.map(folder => ({
      ...folder,
      path: folder.path.trim() // Normalize folder path
    }));
    
    console.log(`[PhotoGallery] Normalized folders:`, folders.map(f => ({ path: f.path, label: f.label })));
    
    // Load images from each folder
    // Only load into the "all" grid - we filter items by folder path when tabs are clicked
    for (const grid of loadingGrids) {
      const folderPath = grid.dataset.cloudinaryFolder;
      if (!folderPath) {
        // This is the "all" grid - load all enabled folders here
        console.log(`[PhotoGallery] Loading all folders into "all" grid`);
        await this.loadAllCloudinaryFolders(grid, folders);
      } else {
        // Skip folder-specific grids - they're not needed for Cloudinary
        // We use the "all" grid and filter items by folder path
        const loadingEl = grid.querySelector('.photo-gallery-loading');
        if (loadingEl) loadingEl.remove();
      }
    }
  }
  
  async loadCloudinaryFolder(grid, folderPath) {
    const loadingEl = grid.querySelector('.photo-gallery-loading');
    const gridGroup = grid.dataset.group;
    
    // Normalize folder path to ensure it matches dropdown values
    const normalizedFolderPath = folderPath.trim();
    
    console.log(`[PhotoGallery] Loading folder "${normalizedFolderPath}" into grid with group "${gridGroup}"`);
    
    try {
      const images = await this.fetchCloudinaryFolderImages(normalizedFolderPath);
      
      console.log(`[PhotoGallery] Loaded ${images.length} images from folder "${normalizedFolderPath}"`);
      
      if (loadingEl) loadingEl.remove();
      
      // Render images with normalized folder path
      images.forEach((image, index) => {
        const item = this.createCloudinaryGalleryItem(image, normalizedFolderPath, index);
        if (item) {
          grid.appendChild(item);
        }
      });
      
      console.log(`[PhotoGallery] Added ${images.length} items to grid "${gridGroup}"`);
      
    } catch (error) {
      console.error('[PhotoGallery] Error loading Cloudinary folder:', error);
      if (loadingEl) {
        loadingEl.innerHTML = `<p>Error loading folder: ${normalizedFolderPath}</p>`;
      }
    }
  }
  
  async loadAllCloudinaryFolders(grid, folders) {
    const loadingEl = grid.querySelector('.photo-gallery-loading');
    const allImages = [];
    
    console.log(`[PhotoGallery] Loading images from ${folders.length} folder(s):`, folders.map(f => f.path));
    
    // Load images from each enabled folder
    for (const folder of folders) {
      try {
        // Normalize folder path to ensure it matches tab values
        const normalizedFolderPath = folder.path.trim();
        if (!normalizedFolderPath) {
          console.warn(`[PhotoGallery] Skipping empty folder path`);
          continue; // Skip empty folder paths
        }
        
        console.log(`[PhotoGallery] Loading folder: "${normalizedFolderPath}"`);
        const images = await this.fetchCloudinaryFolderImages(normalizedFolderPath);
        console.log(`[PhotoGallery] Loaded ${images.length} images from folder "${normalizedFolderPath}"`);
        
        // Store normalized path with each image for filtering
        // IMPORTANT: Use the exact same normalization as tabs use
        // Use asset_folder from Cloudinary response if available, otherwise use requested folder path
        allImages.push(...images.map(img => {
          // Cloudinary returns asset_folder in the response - use that if it exists
          // Otherwise fall back to the folder path we requested
          const imageFolder = (img.asset_folder && img.asset_folder.trim()) || normalizedFolderPath;
          return { ...img, folder: imageFolder.trim() };
        }));
      } catch (error) {
        console.error(`[PhotoGallery] Error loading folder ${folder.path}:`, error);
      }
    }
    
    console.log(`[PhotoGallery] Total images loaded: ${allImages.length}`);
    
    if (loadingEl) loadingEl.remove();
    
    // Render all images into the "all" grid
    allImages.forEach((image, index) => {
      // Use the folder path stored with the image (from asset_folder or requested path)
      // image.folder should always be set from the map function above
      const imageFolderPath = image.folder || '';
      if (!imageFolderPath) {
        console.warn(`[PhotoGallery] Image ${index} has no folder path, skipping`);
        return;
      }
      const item = this.createCloudinaryGalleryItem(image, imageFolderPath, index);
      if (item) {
        grid.appendChild(item);
      }
    });
    
    console.log(`[PhotoGallery] Rendered ${allImages.length} items into grid`);
  }
  
  async fetchCloudinaryFolderImages(folderPath) {
    // Fetch images from Cloudinary folder
    console.log(`[PhotoGallery] Fetching images from folder: ${folderPath}`);
    
    try {
      // Option 1: Use Shopify app proxy (recommended - most secure)
      if (this.cloudinaryProxyUrl) {
        console.log(`[PhotoGallery] Using proxy URL: ${this.cloudinaryProxyUrl}`);
        const proxyUrl = `${this.cloudinaryProxyUrl}?folder=${encodeURIComponent(folderPath)}`;
        const response = await fetch(proxyUrl);
        if (response.ok) {
          const data = await response.json();
          const images = data.resources || data.images || [];
          console.log(`[PhotoGallery] Found ${images.length} images via proxy`);
          return images;
        } else {
          console.error(`[PhotoGallery] Proxy request failed: ${response.status} ${response.statusText}`);
        }
      }
      
      // Option 2: Direct Cloudinary Admin API call (requires credentials)
      // Note: This exposes API keys in the frontend - not recommended for production
      if (this.cloudinaryApiKey && this.cloudinaryApiSecret && this.cloudinaryCloudName) {
        console.log(`[PhotoGallery] Using direct Cloudinary API for cloud: ${this.cloudinaryCloudName}`);
        
        // Generate authentication signature for Cloudinary Admin API
        const timestamp = Math.round(new Date().getTime() / 1000);
        const params = {
          type: 'upload',
          prefix: folderPath,
          max_results: 500,
          api_key: this.cloudinaryApiKey,
          timestamp: timestamp.toString()
        };
        
        // Build query string for signature (sorted keys)
        const sortedKeys = Object.keys(params).sort();
        const queryString = sortedKeys
          .map(key => `${key}=${encodeURIComponent(params[key])}`)
          .join('&');
        
        // Generate SHA-1 signature
        const stringToSign = queryString + this.cloudinaryApiSecret;
        const signature = await this.generateSHA1(stringToSign);
        
        // Add signature to params
        params.signature = signature;
        
        // Build final query string with signature
        const finalQueryString = Object.keys(params)
          .sort()
          .map(key => `${key}=${encodeURIComponent(params[key])}`)
          .join('&');
        
        // Call Cloudinary Admin API
        const apiUrl = `https://api.cloudinary.com/v1_1/${this.cloudinaryCloudName}/resources/image?${finalQueryString}`;
        console.log(`[PhotoGallery] Calling Cloudinary API: ${apiUrl.replace(/api_key=[^&]+/, 'api_key=***').replace(/signature=[^&]+/, 'signature=***')}`);
        
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          const images = data.resources || [];
          console.log(`[PhotoGallery] Found ${images.length} images from Cloudinary API`);
          return images;
        } else {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { message: errorText };
          }
          console.error(`[PhotoGallery] Cloudinary API error (${response.status}):`, errorData);
          return [];
        }
      }
      
      // If no method works, return empty array
      console.warn(`[PhotoGallery] No method configured to fetch Cloudinary folder: ${folderPath}`);
      console.warn(`[PhotoGallery] Proxy URL: ${this.cloudinaryProxyUrl || 'not set'}`);
      console.warn(`[PhotoGallery] API Key: ${this.cloudinaryApiKey ? 'set' : 'not set'}`);
      console.warn(`[PhotoGallery] API Secret: ${this.cloudinaryApiSecret ? 'set' : 'not set'}`);
      console.warn(`[PhotoGallery] Cloud Name: ${this.cloudinaryCloudName || 'not set'}`);
      return [];
      
    } catch (error) {
      console.error('[PhotoGallery] Error fetching Cloudinary folder:', error);
      console.error('[PhotoGallery] Error details:', error.message, error.stack);
      return [];
    }
  }
  
  async generateSHA1(message) {
    // Generate SHA-1 hash using Web Crypto API
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }
  
  createCloudinaryGalleryItem(imageData, folderPath, index) {
    const item = document.createElement('div');
    item.className = 'photo-gallery-item';
    
    // Normalize folder path (trim whitespace, handle both full path and just folder name)
    // IMPORTANT: This must match exactly what's stored in the tab's data-folder-path attribute
    const normalizedFolderPath = folderPath.trim();
    const folderName = normalizedFolderPath.split('/').pop() || normalizedFolderPath;
    
    // Store the folder path for filtering - this MUST match exactly what's in the tabs
    // Use both dataset and setAttribute to ensure it's accessible
    // Use the exact same value as the tab's data-folder-path (no case conversion, no handleization)
    item.dataset.folderPath = normalizedFolderPath;
    item.setAttribute('data-folder-path', normalizedFolderPath);
    item.dataset.group = folderName;
    // Store handleized version to match tab group IDs
    item.dataset.groupSlug = this.handleize(folderName);
    
    // Debug: Log the folder path being set
    if (index < 3) { // Only log first 3 items to avoid spam
      console.log(`[PhotoGallery] Item ${index} - folder path: "${normalizedFolderPath}"`);
    }
    item.dataset.index = index;
    
    // Log for debugging - show more items
    if (index < 5) {
      console.log(`[PhotoGallery] Created item ${index} with folderPath="${normalizedFolderPath}"`);
    }
    
    // Get public_id from imageData (Cloudinary API returns this)
    const publicId = imageData.public_id || imageData.secure_url?.split('/').pop() || '';
    
    if (!publicId) {
      console.warn('No public_id found in imageData:', imageData);
      return null;
    }
    
    // Build Cloudinary URLs with transformations
    const imageUrl = `https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_800,c_limit,q_auto,f_auto/${publicId}`;
    const imageUrlFull = `https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_2000,c_limit,q_90,f_auto/${publicId}`;
    const imageUrlOriginal = `https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/${publicId}`;
    
    // Debug: Log context structure for first few images
    if (index < 3) {
      console.log(`[PhotoGallery] Image ${index} context:`, JSON.stringify(imageData.context, null, 2));
      console.log(`[PhotoGallery] Image ${index} full data:`, JSON.stringify(imageData, null, 2));
    }
    
    // Get title/caption from Cloudinary contextual metadata
    // Based on Cloudinary UI: "Title (caption)" field maps to context.custom.caption
    // Priority: context.custom.caption (Title) > context.caption > context.alt > filename
    const title = imageData.context?.custom?.caption || 
                  imageData.context?.caption ||
                  imageData.context?.alt || 
                  imageData.filename || 
                  publicId.split('/').pop() || 
                  'Gallery image';
    
    // Get description from Cloudinary contextual metadata
    // Based on Cloudinary UI: "Description (alt)" field maps to context.custom.alt
    // Priority: context.custom.alt (Description) > context.description
    const description = imageData.context?.custom?.alt || 
                       imageData.context?.description || 
                       null;
    
    // Get filename for download - ensure it has proper extension
    let filename = imageData.filename || publicId.split('/').pop() || 'image';
    
    // Get format from Cloudinary response (e.g., 'jpg', 'png', 'webp')
    const format = imageData.format || 'jpg';
    
    // Ensure filename has extension
    if (!filename.includes('.')) {
      filename = `${filename}.${format}`;
    } else {
      // If filename has extension but it doesn't match the format, update it
      const currentExt = filename.split('.').pop().toLowerCase();
      const validFormats = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];
      if (!validFormats.includes(currentExt)) {
        filename = filename.replace(/\.[^.]+$/, `.${format}`);
      }
    }
    
    item.innerHTML = `
      <a 
        href="${imageUrlFull}"
        data-lightbox="gallery"
        data-title="${title}${description ? ' - ' + description : ''}"
        class="photo-gallery-item__link"
      >
        <div class="photo-gallery-item__image-wrapper">
          <img 
            src="${imageUrl}"
            srcset="
              https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_400,c_limit,q_auto,f_auto/${publicId} 400w,
              https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_600,c_limit,q_auto,f_auto/${publicId} 600w,
              https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_800,c_limit,q_auto,f_auto/${publicId} 800w,
              https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_1000,c_limit,q_auto,f_auto/${publicId} 1000w,
              https://res.cloudinary.com/${this.cloudinaryCloudName}/image/upload/w_1200,c_limit,q_auto,f_auto/${publicId} 1200w
            "
            sizes="(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw"
            alt="${title}"
            class="photo-gallery-item__image"
            loading="lazy"
          />
          <div class="photo-gallery-item__overlay">
            ${title ? `<div class="photo-gallery-item__caption photo-gallery-item__title">${title}</div>` : ''}
            ${description ? `<div class="photo-gallery-item__description">${description}</div>` : ''}
          </div>
        </div>
      </a>
      <button 
        class="photo-gallery-item__download"
        data-image-url="${imageUrlOriginal}"
        data-image-name="${filename}"
        aria-label="Download ${title}"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 13L10 2M10 13L6 9M10 13L14 9M3 15L17 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
    
    return item;
  }

  setupCSSVariables() {
    const root = this.container;
    root.style.setProperty('--columns-mobile', this.columnsMobile);
    root.style.setProperty('--columns-desktop', this.columnsDesktop);
  }

  collectItems() {
    const grids = this.container.querySelectorAll('.photo-gallery-grid');
    grids.forEach(grid => {
      const items = Array.from(grid.querySelectorAll('.photo-gallery-item'));
      this.allItems = this.allItems.concat(items);
    });
    
    // Initialize visible items based on load method
    if (this.loadMethod === 'all') {
      this.visibleItems = [...this.allItems];
      this.showItems(this.visibleItems);
    } else {
      this.visibleItems = this.allItems.slice(0, this.itemsPerPage);
      this.showItems(this.visibleItems);
      this.hideItems(this.allItems.slice(this.itemsPerPage));
      
      // Update infinite scroll sentinel after collecting items
      if (this.loadMethod === 'infinite_scroll') {
        setTimeout(() => {
          this.updateInfiniteScrollSentinel();
        }, 100);
      }
    }
  }

  setupTabs() {
    if (this.structure !== 'grouped') return;

    const tabs = document.querySelectorAll(`[data-section-id="${this.sectionId}"] .photo-gallery-tab`);
    
    if (!tabs || tabs.length === 0) {
      console.warn('[PhotoGallery] Tabs not found');
      return;
    }

    // Initialize: Apply pagination if needed
    const activeTab = Array.from(tabs).find(tab => tab.classList.contains('active'));
    if (activeTab) {
      // Store the active folder path
      const activeFolderPath = activeTab.dataset.folderPath || activeTab.getAttribute('data-folder-path') || 'all';
      this.activeFolderPath = activeFolderPath;
      
      const allItems = this.container.querySelectorAll('.photo-gallery-item');
      
      if (this.loadMethod === 'all') {
        // Show all items if loadMethod is 'all'
        allItems.forEach(item => {
          item.style.display = '';
          item.style.visibility = 'visible';
          item.style.opacity = '1';
          item.classList.remove('hidden');
        });
        console.log(`[PhotoGallery] Initialized: Showing all ${allItems.length} items (loadMethod: all)`);
      } else {
        // Apply pagination - show first page only
        const visibleItems = Array.from(allItems).slice(0, this.itemsPerPage);
        const hiddenItems = Array.from(allItems).slice(this.itemsPerPage);
        
        visibleItems.forEach(item => {
          item.style.display = '';
          item.style.visibility = 'visible';
          item.style.opacity = '1';
          item.classList.remove('hidden');
        });
        
        hiddenItems.forEach(item => {
          item.style.display = 'none';
          item.style.visibility = 'hidden';
          item.style.opacity = '0';
          item.classList.add('hidden');
        });
        
        // Update load more button visibility
        const loadMoreBtn = document.querySelector(`.photo-gallery-load-more[data-section-id="${this.sectionId}"]`);
        if (hiddenItems.length > 0) {
          if (loadMoreBtn) loadMoreBtn.style.display = '';
        } else {
          if (loadMoreBtn) loadMoreBtn.style.display = 'none';
        }
        
        console.log(`[PhotoGallery] Initialized pagination: ${visibleItems.length} visible, ${hiddenItems.length} hidden (loadMethod: ${this.loadMethod})`);
      }
    }

    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Update active tab
        tabs.forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');

        // Get the folder path from the tab
        const selectedFolderPath = tab.dataset.folderPath || tab.getAttribute('data-folder-path') || '';
        
        console.log(`[PhotoGallery] Tab clicked - Selected folder path: "${selectedFolderPath}"`);
        
        // Store the active folder path for use in loadMore()
        this.activeFolderPath = selectedFolderPath || 'all';
        
        // Reset pagination when switching tabs
        // Each tab should start at page 1 when clicked
        const tabKey = selectedFolderPath || 'all';
        this.currentPage = 1;
        this.currentPageByTab[tabKey] = 1;
        
        // Ensure "all" grid is visible (folder-specific grids are hidden)
        const allGrids = this.container.querySelectorAll('.photo-gallery-grid');
        allGrids.forEach(grid => {
          const gridGroup = grid.dataset.group || '';
          if (gridGroup === 'all') {
            grid.style.display = '';
            grid.classList.add('active');
          } else {
            grid.style.display = 'none';
            grid.classList.remove('active');
          }
        });
        
        // Filter items by folder path
        const allItems = this.container.querySelectorAll('.photo-gallery-item');
        const selectedFolderNormalized = selectedFolderPath.trim().toLowerCase();
        
        console.log(`[PhotoGallery] Filtering items - Selected folder: "${selectedFolderPath}" (normalized: "${selectedFolderNormalized}")`);
        
        // IMPORTANT: First, hide ALL items to reset state from previous tab
        allItems.forEach((item) => {
          item.style.display = 'none';
          item.style.visibility = 'hidden';
          item.style.opacity = '0';
          item.classList.add('hidden');
        });
        
        // Then, filter items by folder
        const filteredItems = [];
        allItems.forEach((item) => {
          const itemFolderPath = item.dataset.folderPath || item.getAttribute('data-folder-path') || '';
          const itemPathNormalized = itemFolderPath.trim().toLowerCase();
          
          // Show item if "all" is selected OR if folder paths match exactly (case-insensitive)
          const shouldShow = selectedFolderPath === 'all' || itemPathNormalized === selectedFolderNormalized;
          
          if (shouldShow) {
            filteredItems.push(item);
          }
        });
        
        console.log(`[PhotoGallery] Filtered ${filteredItems.length} items matching folder "${selectedFolderPath}"`);
        
        // Apply pagination to filtered items
        if (this.loadMethod !== 'all' && filteredItems.length > 0) {
          // Get current page for this specific tab
          const tabKey = selectedFolderPath || 'all';
          const currentPageForTab = this.currentPageByTab[tabKey] || 1;
          const itemsToShow = this.itemsPerPage * currentPageForTab;
          const visibleItems = filteredItems.slice(0, itemsToShow);
          const hiddenItems = filteredItems.slice(itemsToShow);
          
          // IMPORTANT: First, ensure ALL items from other folders are hidden
          allItems.forEach(item => {
            const itemFolderPath = item.dataset.folderPath || item.getAttribute('data-folder-path') || '';
            const itemPathNormalized = itemFolderPath.trim().toLowerCase();
            const matchesFilter = selectedFolderPath === 'all' || itemPathNormalized === selectedFolderNormalized;
            
            if (!matchesFilter) {
              // Force hide items from other folders
              item.style.display = 'none';
              item.style.visibility = 'hidden';
              item.style.opacity = '0';
              item.classList.add('hidden');
            }
          });
          
          // Show first page only
          visibleItems.forEach(item => {
            item.style.display = '';
            item.style.visibility = 'visible';
            item.style.opacity = '1';
            item.classList.remove('hidden');
          });
          
          // Ensure remaining items stay hidden
          hiddenItems.forEach(item => {
            item.style.display = 'none';
            item.style.visibility = 'hidden';
            item.style.opacity = '0';
            item.classList.add('hidden');
          });
          
          // Update load more button visibility
          const loadMoreBtn = document.querySelector(`.photo-gallery-load-more[data-section-id="${this.sectionId}"]`);
          if (hiddenItems.length > 0) {
            if (loadMoreBtn) loadMoreBtn.style.display = '';
          } else {
            if (loadMoreBtn) loadMoreBtn.style.display = 'none';
          }
          
          console.log(`[PhotoGallery] Pagination for "${selectedFolderPath}": Showing ${visibleItems.length} items, ${hiddenItems.length} hidden (page ${currentPageForTab})`);
        } else if (this.loadMethod === 'all') {
          // IMPORTANT: Ensure ALL items from other folders are hidden even in 'all' mode
          allItems.forEach(item => {
            const itemFolderPath = item.dataset.folderPath || item.getAttribute('data-folder-path') || '';
            const itemPathNormalized = itemFolderPath.trim().toLowerCase();
            const matchesFilter = selectedFolderPath === 'all' || itemPathNormalized === selectedFolderNormalized;
            
            if (!matchesFilter) {
              // Force hide items from other folders
              item.style.display = 'none';
              item.style.visibility = 'hidden';
              item.style.opacity = '0';
              item.classList.add('hidden');
            }
          });
          
          // Show all filtered items if loadMethod is 'all'
          filteredItems.forEach(item => {
            item.style.display = '';
            item.style.visibility = 'visible';
            item.style.opacity = '1';
            item.classList.remove('hidden');
          });
        }
        
        // Reapply layout after filtering
        setTimeout(() => {
          this.applyLayout();
          
          // Update infinite scroll sentinel position after filtering
          if (this.loadMethod === 'infinite_scroll') {
            this.updateInfiniteScrollSentinel();
          }
        }, 100);
      });
    });
  }
  
  // Helper function to handleize strings (convert to lowercase, replace spaces/underscores with hyphens)
  handleize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_]+/g, '-')  // Replace spaces and underscores with hyphens
      .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }

  reinitializeGroup(grid) {
    const items = Array.from(grid.querySelectorAll('.photo-gallery-item'));
    
    if (this.loadMethod === 'all') {
      items.forEach(item => item.style.display = '');
    } else {
      // Reset pagination for this group
      this.currentPage = 1;
      const visible = items.slice(0, this.itemsPerPage);
      this.showItems(visible);
      this.hideItems(items.slice(this.itemsPerPage));
    }
    
    // Reapply layout after showing items
    this.applyLayout();
  }

  setupPagination() {
    if (this.loadMethod !== 'pagination') return;

    const loadMoreBtn = document.querySelector(`.photo-gallery-load-more[data-section-id="${this.sectionId}"]`);
    if (!loadMoreBtn) return;

    loadMoreBtn.addEventListener('click', () => {
      if (this.isLoading) return;
      this.loadMore();
    });
  }

  setupInfiniteScroll() {
    if (this.loadMethod !== 'infinite_scroll') return;

    // Create a sentinel element for infinite scroll
    // This will be positioned after the last visible item
    let sentinel = document.querySelector(`.photo-gallery-sentinel[data-section-id="${this.sectionId}"]`);
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.className = 'photo-gallery-sentinel';
      sentinel.setAttribute('data-section-id', this.sectionId);
      sentinel.style.height = '20px';
      sentinel.style.width = '100%';
      sentinel.style.visibility = 'visible';
      sentinel.style.opacity = '0';
      sentinel.style.pointerEvents = 'none';
      sentinel.style.display = 'block';
      
      // Insert into the active grid
      const activeGrid = this.container.querySelector('.photo-gallery-grid.active');
      if (activeGrid) {
        activeGrid.appendChild(sentinel);
      } else {
        // Fallback: insert after container
        this.container.appendChild(sentinel);
      }
    }
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading) {
          console.log('[PhotoGallery] Sentinel intersecting, loading more...');
          this.loadMore();
        }
      });
    }, {
      rootMargin: '200px',
      threshold: 0.1
    });

    // Observe the sentinel
    observer.observe(sentinel);
    
    // Store observer and sentinel for later updates
    this.infiniteScrollObserver = observer;
    this.infiniteScrollSentinel = sentinel;
    
    // Update sentinel position after initial setup
    setTimeout(() => {
      this.updateInfiniteScrollSentinel();
    }, 200);
  }
  
  updateInfiniteScrollSentinel() {
    if (this.loadMethod !== 'infinite_scroll' || !this.infiniteScrollSentinel) return;
    
    const activeGrid = this.container.querySelector('.photo-gallery-grid.active');
    if (!activeGrid) return;
    
    // Use stored active folder path (set when tab is clicked) instead of querying DOM
    const selectedFolderPath = this.activeFolderPath || 'all';
    const selectedFolderNormalized = selectedFolderPath.trim().toLowerCase();
    
    console.log(`[PhotoGallery] updateInfiniteScrollSentinel() - Using stored active folder: "${selectedFolderPath}"`);
    
    // Get current page for this specific tab
    const tabKey = selectedFolderPath || 'all';
    const currentPageForTab = this.currentPageByTab[tabKey] || 1;
    
    // Get all items in the grid
    const allItems = Array.from(activeGrid.querySelectorAll('.photo-gallery-item'));
    
    // Filter to only items that match the current folder filter
    const filteredItems = allItems.filter(item => {
      if (selectedFolderPath === 'all') return true;
      const itemFolderPath = item.dataset.folderPath || item.getAttribute('data-folder-path') || '';
      const itemPathNormalized = itemFolderPath.trim().toLowerCase();
      return itemPathNormalized === selectedFolderNormalized;
    });
    
    // Calculate how many items should be visible based on current page for this tab
    const itemsToShowCount = this.itemsPerPage * currentPageForTab;
    const visibleItems = filteredItems.slice(0, itemsToShowCount);
    const hiddenItems = filteredItems.slice(itemsToShowCount);
    
    const sentinel = this.infiniteScrollSentinel;
    
    if (visibleItems.length > 0 && hiddenItems.length > 0) {
      // Position sentinel after the last visible item
      const lastItem = visibleItems[visibleItems.length - 1];
      
      // Move sentinel to be right after the last item
      if (sentinel.parentElement !== activeGrid) {
        activeGrid.appendChild(sentinel);
      } else {
        // Re-position if already in grid - use insertBefore to ensure correct order
        const nextSibling = lastItem.nextSibling;
        if (nextSibling !== sentinel) {
          // Remove sentinel from current position
          sentinel.remove();
          // Insert after last visible item
          if (nextSibling) {
            activeGrid.insertBefore(sentinel, nextSibling);
          } else {
            activeGrid.appendChild(sentinel);
          }
        }
      }
      
      // Make sure sentinel is visible (for IntersectionObserver)
      sentinel.style.display = 'block';
      sentinel.style.height = '20px';
      sentinel.style.width = '100%';
      sentinel.style.visibility = 'visible';
      sentinel.style.opacity = '0';
      sentinel.style.pointerEvents = 'none';
      
      console.log(`[PhotoGallery] Updated infinite scroll sentinel for "${selectedFolderPath}" (${visibleItems.length} visible, ${hiddenItems.length} hidden, page ${this.currentPage})`);
    } else {
      // No more items, hide sentinel
      sentinel.style.display = 'none';
      const loadingEl = document.querySelector(`.photo-gallery-loading[data-section-id="${this.sectionId}"]`);
      if (loadingEl) loadingEl.style.display = 'none';
      console.log(`[PhotoGallery] No more items for "${selectedFolderPath}", hiding sentinel`);
    }
  }

  loadMore() {
    if (this.isLoading) return;
    
    const activeGrid = this.container.querySelector('.photo-gallery-grid.active');
    if (!activeGrid) return;

    // Use stored active folder path (set when tab is clicked) instead of querying DOM
    const selectedFolderPath = this.activeFolderPath || 'all';
    const selectedFolderNormalized = selectedFolderPath.trim().toLowerCase();
    
    console.log(`[PhotoGallery] loadMore() - Using stored active folder: "${selectedFolderPath}"`);
    
    // Get all items in the active grid
    const allItems = Array.from(activeGrid.querySelectorAll('.photo-gallery-item'));
    
    // Filter to only items that match the current folder filter
    const filteredItems = allItems.filter(item => {
      if (selectedFolderPath === 'all') return true;
      const itemFolderPath = item.dataset.folderPath || item.getAttribute('data-folder-path') || '';
      const itemPathNormalized = itemFolderPath.trim().toLowerCase();
      return itemPathNormalized === selectedFolderNormalized;
    });
    
    // Get current page for this specific tab
    const tabKey = selectedFolderPath || 'all';
    const currentPageForTab = this.currentPageByTab[tabKey] || 1;
    
    console.log(`[PhotoGallery] loadMore() called for "${selectedFolderPath}" - Current page: ${currentPageForTab}, Filtered items: ${filteredItems.length}`);
    
    // Calculate how many items should currently be visible based on current page
    const currentItemsToShowCount = this.itemsPerPage * currentPageForTab;
    const currentlyVisibleItems = filteredItems.slice(0, currentItemsToShowCount);
    
    // Calculate next page
    const nextPage = currentPageForTab + 1;
    const nextItemsToShowCount = this.itemsPerPage * nextPage;
    const nextVisibleItems = filteredItems.slice(0, nextItemsToShowCount);
    
    // Items to show are the difference between next page and current page
    const newItemsToShow = nextVisibleItems.slice(currentlyVisibleItems.length);
    
    console.log(`[PhotoGallery] Currently showing: ${currentlyVisibleItems.length}, Next page will show: ${nextVisibleItems.length}, New items to show: ${newItemsToShow.length}`);
    
    if (newItemsToShow.length === 0) {
      // No more items
      const loadMoreBtn = document.querySelector(`.photo-gallery-load-more[data-section-id="${this.sectionId}"]`);
      const loadingEl = document.querySelector(`.photo-gallery-loading[data-section-id="${this.sectionId}"]`);
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      if (loadingEl) loadingEl.style.display = 'none';
      if (this.infiniteScrollSentinel) this.infiniteScrollSentinel.style.display = 'none';
      console.log(`[PhotoGallery] No more items to load for "${selectedFolderPath}"`);
      return;
    }

    this.isLoading = true;
    const loadingEl = document.querySelector(`.photo-gallery-loading[data-section-id="${this.sectionId}"]`);
    if (loadingEl) loadingEl.style.display = 'block';

    // Simulate loading delay for better UX
    setTimeout(() => {
      // IMPORTANT: First, ensure ALL items from other folders are hidden
      allItems.forEach(item => {
        const itemFolderPath = item.dataset.folderPath || item.getAttribute('data-folder-path') || '';
        const itemPathNormalized = itemFolderPath.trim().toLowerCase();
        const matchesFilter = selectedFolderPath === 'all' || itemPathNormalized === selectedFolderNormalized;
        
        if (!matchesFilter) {
          // Force hide items from other folders
          item.style.display = 'none';
          item.style.visibility = 'hidden';
          item.style.opacity = '0';
          item.classList.add('hidden');
        }
      });
      
      // Show all items up to the next page (including previously visible ones)
      nextVisibleItems.forEach(item => {
        item.style.display = '';
        item.style.visibility = 'visible';
        item.style.opacity = '1';
        item.classList.remove('hidden');
      });
      
      // Hide items beyond the next page
      const remainingHiddenItems = filteredItems.slice(nextItemsToShowCount);
      remainingHiddenItems.forEach(item => {
        item.style.display = 'none';
        item.style.visibility = 'hidden';
        item.style.opacity = '0';
        item.classList.add('hidden');
      });
      
      // Update page for this specific tab
      this.currentPageByTab[tabKey] = nextPage;
      this.currentPage = nextPage;
      this.isLoading = false;
      if (loadingEl) loadingEl.style.display = 'none';
      
      // Check if there are more items
      if (remainingHiddenItems.length <= 0) {
        const loadMoreBtn = document.querySelector(`.photo-gallery-load-more[data-section-id="${this.sectionId}"]`);
        if (loadMoreBtn) loadMoreBtn.style.display = 'none';
        if (loadingEl) loadingEl.style.display = 'none';
        if (this.infiniteScrollSentinel) this.infiniteScrollSentinel.style.display = 'none';
      }
      
      this.applyLayout();
      
      // Update infinite scroll sentinel position after loading more
      if (this.loadMethod === 'infinite_scroll') {
        setTimeout(() => {
          this.updateInfiniteScrollSentinel();
        }, 100);
      }
      
      console.log(`[PhotoGallery] Loaded ${newItemsToShow.length} more items for "${selectedFolderPath}" (now showing ${nextVisibleItems.length} total, page ${nextPage})`);
    }, 300);
  }

  showItems(items) {
    items.forEach(item => {
      item.style.display = '';
    });
  }

  hideItems(items) {
    items.forEach(item => {
      item.style.display = 'none';
    });
  }

  setupDownloads() {
    // Helper function to ensure filename has extension
    const ensureFileExtension = (filename, url) => {
      if (!filename || filename === 'image') {
        // Extract from Cloudinary URL
        // Format: https://res.cloudinary.com/cloud_name/image/upload/public_id
        const urlMatch = url.match(/\/image\/upload\/(?:[^\/]+\/)?([^\/\?]+)/);
        if (urlMatch) {
          filename = urlMatch[1];
        } else {
          // Fallback: extract from URL path
          const urlParts = url.split('/');
          filename = urlParts[urlParts.length - 1].split('?')[0];
        }
      }
      
      // Remove any Cloudinary transformations that might be in the filename
      filename = filename.replace(/^(w_\d+|h_\d+|c_\w+|q_\w+|f_\w+)_/, '');
      
      // If no extension, try to detect from URL or default to jpg
      if (!filename.includes('.')) {
        // Check if URL has format parameter (Cloudinary)
        const formatMatch = url.match(/f_(\w+)/);
        const format = formatMatch ? formatMatch[1] : 'jpg';
        filename = `${filename}.${format}`;
      }
      
      return filename;
    };
    
    // Handle download buttons
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('.photo-gallery-item__download')) {
        e.preventDefault();
        e.stopPropagation();
        
        const btn = e.target.closest('.photo-gallery-item__download');
        const imageUrl = btn.dataset.imageUrl;
        let imageName = btn.dataset.imageName || 'image';
        
        // Ensure filename has extension
        imageName = ensureFileExtension(imageName, imageUrl);
        
        console.log('[PhotoGallery] Downloading:', { imageUrl, imageName });
        
        // Use fetch to download the image as a blob to avoid CORS issues
        fetch(imageUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.blob();
          })
          .then(blob => {
            // Determine MIME type from filename extension
            const ext = imageName.split('.').pop().toLowerCase();
            const mimeTypes = {
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'webp': 'image/webp',
              'gif': 'image/gif',
              'svg': 'image/svg+xml'
            };
            const mimeType = mimeTypes[ext] || blob.type || 'image/jpeg';
            
            // Create object URL and download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = imageName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up object URL after a delay
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 100);
          })
          .catch(error => {
            console.error('[PhotoGallery] Download error:', error);
            // Fallback: try direct download link
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = imageName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          });
      }
    });

    // Add download button to Lightbox
    if (typeof lightbox !== 'undefined') {
      const originalEnd = lightbox.end;
      lightbox.end = function() {
        originalEnd.call(this);
        this.addDownloadButton();
      };

      lightbox.addDownloadButton = function() {
        const container = document.querySelector('.lb-dataContainer');
        if (!container) return;

        // Remove existing download button
        const existing = container.querySelector('.lb-download');
        if (existing) existing.remove();

        const imageLink = document.querySelector('.lb-image');
        if (!imageLink) return;

        const imageUrl = imageLink.getAttribute('src');
        
        // Helper to ensure filename has extension
        const ensureFileExtension = (filename, url) => {
          if (!filename || filename === 'image') {
            // Extract from Cloudinary URL
            const urlMatch = url.match(/\/image\/upload\/(?:[^\/]+\/)?([^\/\?]+)/);
            if (urlMatch) {
              filename = urlMatch[1];
            } else {
              const urlParts = url.split('/');
              filename = urlParts[urlParts.length - 1].split('?')[0];
            }
          }
          
          // Remove Cloudinary transformations from filename if present
          filename = filename.replace(/^(w_\d+|h_\d+|c_\w+|q_\w+|f_\w+)_/, '');
          if (filename.includes('/')) {
            filename = filename.split('/').pop();
          }
          
          // Remove query parameters
          filename = filename.split('?')[0];
          
          // If no extension, try to detect from URL or default to jpg
          if (!filename.includes('.')) {
            // Check if URL has format parameter (Cloudinary)
            const formatMatch = url.match(/f_(\w+)/);
            const format = formatMatch ? formatMatch[1] : 'jpg';
            filename = `${filename}.${format}`;
          }
          
          return filename;
        };
        
        let imageName = imageUrl.split('/').pop() || 'image';
        imageName = ensureFileExtension(imageName, imageUrl);

        const downloadBtn = document.createElement('a');
        downloadBtn.href = imageUrl;
        downloadBtn.className = 'lb-download';
        downloadBtn.textContent = 'Download';
        downloadBtn.download = imageName;
        downloadBtn.onclick = function(e) {
          e.preventDefault();
          
          // Use fetch to download the image as a blob to avoid CORS issues
          fetch(imageUrl)
            .then(response => {
              if (!response.ok) {
                throw new Error('Network response was not ok');
              }
              return response.blob();
            })
            .then(blob => {
              // Determine MIME type from filename extension
              const ext = imageName.split('.').pop().toLowerCase();
              const mimeTypes = {
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png',
                'webp': 'image/webp',
                'gif': 'image/gif',
                'svg': 'image/svg+xml'
              };
              const mimeType = mimeTypes[ext] || blob.type || 'image/jpeg';
              
              // Create object URL and download
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = imageName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up object URL after a delay
              setTimeout(() => {
                window.URL.revokeObjectURL(url);
              }, 100);
            })
            .catch(error => {
              console.error('[PhotoGallery] Download error:', error);
              // Fallback: try direct download link
              const link = document.createElement('a');
              link.href = imageUrl;
              link.download = imageName;
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            });
        };

        const dataContainer = container.querySelector('.lb-data');
        if (dataContainer) {
          dataContainer.appendChild(downloadBtn);
        }
      };
    }
  }

  setupLightbox() {
    // Wait for Lightbox2 and jQuery to be available
    const initLightbox = () => {
      if (typeof lightbox !== 'undefined' && (typeof jQuery !== 'undefined' || typeof $ !== 'undefined')) {
        lightbox.option({
          'resizeDuration': 200,
          'wrapAround': true,
          'fadeDuration': 300,
          'imageFadeDuration': 300,
          'showImageNumberLabel': true,
          'albumLabel': 'Image %1 of %2'
        });
      } else {
        // Retry after a short delay
        setTimeout(initLightbox, 100);
      }
    };
    initLightbox();
  }

  applyLayout() {
    // Layout-specific initialization
    switch (this.layout) {
      case 'masonry':
        this.initMasonry();
        break;
      case 'justified':
        this.initJustified();
        break;
      case 'mosaic':
        this.initMosaic();
        break;
    }
  }

  initMasonry() {
    // Masonry layout is handled by CSS columns
    // Just ensure images load properly
    const images = this.container.querySelectorAll('.photo-gallery-item__image');
    images.forEach(img => {
      if (img.complete) {
        this.masonryLayout();
      } else {
        img.addEventListener('load', () => {
          this.masonryLayout();
        });
      }
    });
  }

  masonryLayout() {
    // Additional masonry adjustments if needed
    // CSS columns handle most of this
  }

  initJustified() {
    // Justified layout is handled by CSS flexbox
    // Could add JavaScript for more precise row heights if needed
  }

  initMosaic() {
    // Mosaic layout is handled by CSS grid
    // Could add JavaScript for dynamic sizing if needed
  }
}

// Initialize galleries when DOM is ready
function initPhotoGalleries() {
  console.log('[PhotoGallery] Initializing galleries...');
  const galleries = document.querySelectorAll('.photo-gallery-container');
  console.log(`[PhotoGallery] Found ${galleries.length} gallery container(s)`);
  
  galleries.forEach((container, index) => {
    // Check if already initialized
    if (!container.dataset.initialized) {
      console.log(`[PhotoGallery] Initializing gallery ${index + 1}...`);
      container.dataset.initialized = 'true';
      try {
        new PhotoGallery(container);
        console.log(`[PhotoGallery] Gallery ${index + 1} initialized successfully`);
      } catch (error) {
        console.error(`[PhotoGallery] Error initializing gallery ${index + 1}:`, error);
      }
    } else {
      console.log(`[PhotoGallery] Gallery ${index + 1} already initialized, skipping`);
    }
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPhotoGalleries);
} else {
  // DOM already loaded
  initPhotoGalleries();
}

// Reinitialize on dynamic content load
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    console.log('[PhotoGallery] Section loaded event triggered');
    const container = event.target.querySelector('.photo-gallery-container');
    if (container && !container.dataset.initialized) {
      console.log('[PhotoGallery] Initializing gallery from section load event');
      container.dataset.initialized = 'true';
      try {
        new PhotoGallery(container);
      } catch (error) {
        console.error('[PhotoGallery] Error initializing gallery from section load:', error);
      }
    }
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    console.log('[PhotoGallery] Section reordered, reinitializing...');
    initPhotoGalleries();
  });
}

} // End of PhotoGallery class wrapper
